import React, { useEffect, useState } from 'react'
import { Form, message } from "antd";
import SOURCES from '../../../../utils/constants';
import CommonForm from '../../../global/form';
import CommonDialog from '../../../global/dialog';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';

const AddAffiliates = ({ editDeleteAdd, setEditDeleteAdd, keyParams }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    // Query for fetching affiliate details if in edit mode
    const { data: affiliateDetail, isInitialLoading: isLoadingDetail } = useGenericQuery({
        queryKey: QUERY_KEYS.affiliates.item(editDeleteAdd.id),
        endpoint: `affiliate/detail?id=${editDeleteAdd.id}`,
        enabled: !!editDeleteAdd.id && !!editDeleteAdd.isEditable,
    });

    // Populate form when detail is loaded
    useEffect(() => {
        if (affiliateDetail && editDeleteAdd.id) {
            form.setFieldsValue({
                name: affiliateDetail.name,
                email: affiliateDetail.email,
                phone: affiliateDetail.phone,
                companyName: affiliateDetail.companyName,
                companyUrl: affiliateDetail.companyUrl,
                bussinessType: affiliateDetail.bussinessType,
                commissionType: affiliateDetail.commissionType,
                commissionValue: affiliateDetail.commissionValue,
                userType: affiliateDetail.userType,
                additionalInformation: affiliateDetail.additionalInformation,
                emailStripeOnboardLink: affiliateDetail.emailStripeOnboardLink === 'y',
            });
        }
    }, [affiliateDetail, editDeleteAdd.id, form]);


    const addAffiliateMutation = useGenericMutation({
        endpoint: "affiliate",
        method: "post",
        onSuccess: () => {
            message.success(editDeleteAdd.id ? "Affiliate updated successfully" : "Affiliate added successfully");
            setEditDeleteAdd({ ...editDeleteAdd, editAdd: false, id: null });
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.affiliates.lists({ ...keyParams }),
                type: "active",
            });
            form.resetFields();
        },
        onError: (err) => {
            console.error(err);
            message.error(err?.response?.data?.message || "Something went wrong");
        }
    });

    const handleSubmit = () => {
        form.validateFields()
            .then(async (values) => {
                const payload = {
                    ...values,
                    emailStripeOnboardLink: values.emailStripeOnboardLink ? "y" : "n",
                    id: editDeleteAdd.id // For update if ID exists
                };

                // If it's an update, the backend usually expects the ID in the body (based on old code)
                // and usePost usually defaults to POST. If endpoint 'affiliate' handles PUT logic or ID check, this is fine.
                // Assuming standard behavior of this backend based on other components.
                await addAffiliateMutation.mutateAsync(payload);
            })
            .catch((info) => {
                console.log('Validate Failed:', info);
            });
    };

    const formData = [
        {
            label: "Name",
            name: "name",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Name",
            required: true,
            message: "Name is required.",
            span: 12
        },
        {
            label: "Email",
            name: "email",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "email",
            placeholder: "Email Address",
            required: true,
            message: "Email is required.",
            span: 12
        },
        {
            label: "Phone",
            name: "phone",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Phone Number",
            required: false,
            span: 12
        },
        {
            label: "Company Name",
            name: "companyName",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Company Name",
            required: false,
            span: 12
        },
        {
            label: "Company URL",
            name: "companyUrl",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "url",
            placeholder: "Company URL",
            required: false,
            span: 12
        },
        {
            label: "Business Type",
            name: "bussinessType",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Business Type",
            required: false,
            span: 12
        },
        {
            label: "Commission Type",
            name: "commissionType",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            type: "select",
            placeholder: "Commission Type",
            required: true,
            message: "Commission type is required.",
            options: [
                { label: "Flat Fee", value: "flat" },
                { label: "Percentage", value: "percentage" },
            ],
            span: 12
        },
        {
            label: "Commission Value",
            name: "commissionValue",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "number",
            placeholder: "Commission Value",
            required: true,
            message: "Commission value is required.",
            span: 12
        },
        {
            label: "Type",
            name: "userType",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            type: "select",
            placeholder: "Type",
            required: true,
            message: "Type is required.",
            options: [
                { label: "Affiliate", value: "affiliate" },
                { label: "Partner", value: "partner" },
            ],
            span: 24
        },
        {
            label: "Please add any additional information so we can understand how you will be promoting the affiliation/partnership with LeadsImport and how we can support your efforts",
            name: "additionalInformation",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "textarea",
            placeholder: "Additional information",
            required: false,
            span: 24
        },
        {
            label: "Do you want to send Stripe onboarding link to affiliate/partner?",
            name: "emailStripeOnboardLink",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "checkbox",
            required: false,
            span: 24
        }
    ];

    return (
        <CommonDialog
            open={editDeleteAdd.editAdd}
            onCancel={() => {
                form.resetFields();
                setEditDeleteAdd({ ...editDeleteAdd, editAdd: false, id: null });
            }}
            title={editDeleteAdd.isEditable ? "Edit Affiliate" : "Add Affiliate"}
            description={editDeleteAdd.isEditable ? "Modify the existing affiliate details and save your changes." : "Fill the below form for add a new affiliate."}
            onOk={handleSubmit}
            isConfirmButtonLoading={addAffiliateMutation.isPending}
            okButtonTitle={editDeleteAdd.isEditable ? "Update" : "Confirm"}
            className='relative '
            contentClassName={(isLoadingDetail && editDeleteAdd.isEditable) && "!overflow-hidden"}
        >
            {(isLoadingDetail && editDeleteAdd.isEditable) && (
                <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
                    <div className="flex flex-col justify-center items-center gap-1">
                        <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
                    </div>
                </div>
            )}
            <CommonForm
                data={formData}
                className="mt-4"
                layoutType="vertical"
                form={form}
            />
        </CommonDialog>
    )
}

export default AddAffiliates;